import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { motion } from 'framer-motion';
import { Trash2, Plus, Minus, ShoppingCart, ArrowLeft, ArrowRight } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

export default function CartPage() {
  const { t, lang } = useLanguage();
  const { items, subtotal, taxes, total, itemCount, removeItem, updateQuantity, clearCart } = useCart();

  if (items.length === 0) {
    return (
      <div className="pt-20 lg:pt-24 pb-20 min-h-[60vh] flex items-center justify-center" data-testid="cart-page-empty">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 text-[#e0e0e0] mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-semibold text-[#1a1a1a] mb-2">{t('cart.empty')}</h2>
          <Link to="/menu" className="inline-flex items-center gap-2 mt-4 text-[#e53a3a] font-semibold hover:underline" data-testid="continue-shopping">
            <ArrowLeft className="w-4 h-4" /> {t('cart.continueShopping')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 lg:pt-24 pb-20" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-heading text-4xl sm:text-5xl font-bold text-[#1a1a1a] tracking-tight mb-8"
        >
          {t('cart.title')} <span className="text-[#555555] text-lg font-body">({itemCount} {t('cart.items')})</span>
        </motion.h1>

        <div className="space-y-4 mb-8">
          {items.map((item) => {
            const name = lang === 'fr' ? item.menuItem.name_fr : item.menuItem.name_en;
            const modsPrice = (item.modifiers || []).reduce((s, m) => s + (m.price || 0), 0);
            const price = item.menuItem.price + (item.selectedOption?.price_modifier || 0) + modsPrice;
            return (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-4 bg-white rounded-xl border border-[#e0e0e0] p-4"
                data-testid={`cart-item-${item.key}`}
              >
                {item.menuItem.image_url ? (
                  <img src={item.menuItem.image_url} alt={name} className="w-20 h-20 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-[#f5f5f0] shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-lg font-semibold text-[#1a1a1a] truncate">{name}</h3>
                  {item.selectedOption && (
                    <p className="text-xs text-[#555555]">{lang === 'fr' ? item.selectedOption.name_fr : item.selectedOption.name_en}</p>
                  )}
                  {(item.modifiers || []).length > 0 && (
                    <ul className="text-xs text-[#888] mt-1">
                      {item.modifiers.map((m, idx) => (
                        <li key={idx}>+ {lang === 'fr' ? m.name_fr : m.name_en}{m.price > 0 ? ` (+${m.price.toFixed(2)} $)` : ''}</li>
                      ))}
                    </ul>
                  )}
                  <p className="text-sm font-medium text-[#e53a3a] mt-1">{price.toFixed(2)} $</p>
                </div>
                <div className="flex items-center border border-[#e0e0e0] rounded-lg">
                  <button onClick={() => updateQuantity(item.key, item.quantity - 1)} className="p-2 hover:bg-[#f5f5f0] rounded-l-lg" data-testid={`cart-minus-${item.key}`}>
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-3 text-sm font-medium">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.key, item.quantity + 1)} className="p-2 hover:bg-[#f5f5f0] rounded-r-lg" data-testid={`cart-plus-${item.key}`}>
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <span className="font-semibold text-[#1a1a1a] w-20 text-right">{(price * item.quantity).toFixed(2)} $</span>
                <button onClick={() => removeItem(item.key)} className="p-2 text-[#D32F2F] hover:bg-red-50 rounded-lg" data-testid={`cart-remove-${item.key}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="bg-white rounded-xl border border-[#e0e0e0] p-6" data-testid="cart-summary">
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-[#555555]">{t('cart.subtotal')}</span>
              <span className="font-medium">{subtotal.toFixed(2)} $</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#555555]">{t('cart.taxes')}</span>
              <span className="font-medium">{taxes.toFixed(2)} $</span>
            </div>
            <Separator />
            <div className="flex justify-between text-lg font-bold">
              <span>{t('cart.total')}</span>
              <span className="text-[#e53a3a]">{total.toFixed(2)} $</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Link
              to="/menu"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold border-2 border-[#e0e0e0] text-[#1a1a1a] hover:bg-[#f5f5f0] transition-colors"
              data-testid="cart-continue"
            >
              <ArrowLeft className="w-4 h-4" /> {t('cart.continueShopping')}
            </Link>
            <Link
              to="/checkout"
              className="flex-1 btn-belgo flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-white"
              style={{ backgroundColor: '#e53a3a' }}
              data-testid="cart-checkout"
            >
              {t('cart.checkout')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
